import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, trackEvent } from "../../lib/api";
import { postcodeError, validateContact } from "../wizard/wizardOptions";
import { COMPARE_DONE_KEY, COMPARE_STEPS, buildComparePayload, itemError, newItem, offerStepError } from "./compareOptions";
import { CompareContactStep, OfferStep, SizesStep, SpecsStep } from "./CompareSteps";

const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);
const EMPTY = { price: "", vat: null, competitor: "", goal: null, product: null, profile_system: "", glazing: null, color: null, includes: [],
  notes: "", naam: "", email: "", telefoon: "", postcode: "", woonplaats: "", website: "" };

const apiError = (err) => {
  const d = err?.response?.data?.detail;
  if (err?.response?.status === 429) return "Te veel pogingen achter elkaar. Probeer het over een minuut opnieuw.";
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg.replace(/^Value error, /, "");
  return "Versturen is niet gelukt. Controleer uw verbinding en probeer het opnieuw, of bel ons.";
};

const Progress = ({ step }) => (
  <div className="mb-7" data-testid="compare-progress">
    <div className="flex items-center justify-between text-[12.5px] font-semibold">
      <span className="uppercase tracking-[0.14em] text-gold">Stap {step} van {COMPARE_STEPS.length}</span>
      <span className="text-slate-500" data-testid="compare-step-label">{COMPARE_STEPS[step - 1].short}</span>
    </div>
    <ol className="mt-3 grid grid-cols-4 gap-1.5" aria-hidden="true">
      {COMPARE_STEPS.map((s, i) => <li key={s.short} className={`h-1.5 rounded-full transition-colors duration-300 ${i < step ? "bg-maas" : "bg-slate-200"}`} />)}
    </ol>
  </div>
);

export const QuoteCompareForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const topRef = useRef(null);
  const meta = useRef({ key: newKey(), startedAt: Date.now(), source: location.state?.from || location.pathname });
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [offers, setOffers] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [items, setItems] = useState(() => [newItem()]);
  const [errors, setErrors] = useState({});
  const [offerError, setOfferError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined })); if (offerError) setOfferError(""); };

  const prevStep = useRef(step);
  useEffect(() => {
    if (prevStep.current === step) return;
    prevStep.current = step;
    const el = topRef.current;
    if (el && el.getBoundingClientRect().top < 80) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [step]);

  const uploading = [...offers, ...photos].some((u) => !u.path);
  const blocked = uploading || (step === 3 && items.some(itemError)) || (step === 4 && !!postcodeError(form.postcode));
  const next = () => {
    if (blocked) return;
    if (step === 1) { const e = offerStepError(form, offers); setOfferError(e); if (e) return; }
    trackEvent("compare_step", { step, step_name: COMPARE_STEPS[step - 1].short });
    setStep((s) => s + 1);
  };

  const submit = async () => {
    const errs = validateContact(form);
    setErrors(errs);
    if (Object.keys(errs).length || submitting || blocked) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/quote-comparisons", buildComparePayload({ form, offers, photos, items, meta: meta.current }));
      sessionStorage.setItem(COMPARE_DONE_KEY, JSON.stringify({ lead_number: data.lead_number, product_category: form.product || "anders", tracked: false }));
      navigate("/offerte-vergelijken/bedankt");
    } catch (err) {
      toast.error(apiError(err));
      setSubmitting(false);
    }
  };

  const last = step === COMPARE_STEPS.length;
  const body = [
    <OfferStep key="1" form={form} set={set} offers={offers} setOffers={(u) => { setOffers(u); setOfferError(""); }} error={offerError} />,
    <SpecsStep key="2" form={form} set={set} />,
    <SizesStep key="3" form={form} set={set} items={items} setItems={setItems} photos={photos} setPhotos={setPhotos} />,
    <CompareContactStep key="4" form={form} set={set} errors={errors} />,
  ][step - 1];
  return (
    <div ref={topRef} id="formulier" data-testid="compare-form" className="scroll-mt-28 rounded-3xl border border-slate-200 bg-white p-5 sm:p-8" style={{ boxShadow: "var(--shadow-2)" }}>
      <Progress step={step} />
      <h2 className="text-[22px] font-bold leading-tight text-ink sm:text-[26px]" data-testid="compare-step-title">{COMPARE_STEPS[step - 1].title}</h2>
      {step === 1 && <p className="mt-2 text-[14.5px] text-slate-500">Vul het bedrag in of upload uw offerte. Eén van beide is genoeg.</p>}
      <form className="mt-6" noValidate onSubmit={(e) => { e.preventDefault(); last ? submit() : next(); }}>
        {body}
        <div className="mt-8 flex flex-col-reverse gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
          {step > 1 ? (
            <button type="button" onClick={() => setStep((s) => s - 1)} disabled={submitting} data-testid="compare-back"
              className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl px-3 text-[15px] font-semibold text-slate-600 hover:bg-slate-50 hover:text-ink disabled:opacity-40">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Vorige
            </button>
          ) : <span className="hidden sm:block" />}
          <button type="submit" disabled={blocked || submitting} data-testid={last ? "compare-submit" : "compare-next"}
            className={`${last ? "btn-cta" : "btn-primary min-h-[52px]"} w-full whitespace-nowrap sm:w-auto disabled:cursor-not-allowed disabled:opacity-50`}>
            {last ? (submitting ? <><Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Bezig met versturen…</> : <><Check className="h-5 w-5" aria-hidden="true" /> Offerte laten vergelijken</>)
              : <>{uploading ? "Bezig met uploaden…" : "Volgende"} <ArrowRight className="h-4 w-4" aria-hidden="true" /></>}
          </button>
        </div>
        {last && <p className="mt-3 flex items-center justify-center gap-1.5 text-[12.5px] text-slate-500 sm:justify-end"><Lock className="h-3.5 w-3.5" aria-hidden="true" /> Vrijblijvend · uw offerte wordt vertrouwelijk behandeld</p>}
      </form>
    </div>
  );
};
